import { EMedicalCategoryType } from '@/entities/medical-category';
import { useSearchDoctorsByTimeSlotV2Query } from '@/hooks/telemedicine/telemedicine.gql.impl';
import { GeoPoint } from '@/models/diagnosis';
import { EDoctorSortType, NonF2fDoctor, NonF2fDoctorReviewInfo } from '@/models/doctor/telemedicine';
import {
  DiagnosisType,
  DoctorSortType,
  MedicalCategoryType,
  SearchDoctorsByTimeSlotV2Query,
} from '@/types/bff/gql-schema';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';

interface Props {
  categoryType?: EMedicalCategoryType;
  categoryId?: string;
  targetDate: string;
  startTimeSlotValue: number;
  endTimeSlotValue: number;
  sortType?: EDoctorSortType;
  specialistOnly?: boolean;
  highReviewRating?: boolean;
  highReviewCount?: boolean;
  geoPoint?: GeoPoint;
  enabled?: boolean;
}

const parseCategoryType = (categoryType?: EMedicalCategoryType) => {
  switch (categoryType) {
    case EMedicalCategoryType.TREATMENT:
      return MedicalCategoryType.Treatment;
    case EMedicalCategoryType.DEPARTMENT:
      return MedicalCategoryType.Department;
    case EMedicalCategoryType.VACCINATION:
      return MedicalCategoryType.Vaccination;
    default:
      return MedicalCategoryType.Department;
  }
};

const parseSortType = (sortType?: EDoctorSortType): DoctorSortType => {
  switch (sortType) {
    case EDoctorSortType.DEFAULT:
      return DoctorSortType.Default;
    case EDoctorSortType.DISTANCE:
      return DoctorSortType.Distance;
    case EDoctorSortType.REVIEW_COUNT:
      return DoctorSortType.ReviewCount;
    case EDoctorSortType.REVIEW_AVERAGE_RATING:
      return DoctorSortType.ReviewAverageRating;
    default:
      return DoctorSortType.Default;
  }
};

const parseDiagnosisType = (categoryType?: EMedicalCategoryType): DiagnosisType | undefined => {
  switch (categoryType) {
    case EMedicalCategoryType.TREATMENT:
      return DiagnosisType.Treatment;
    case EMedicalCategoryType.DEPARTMENT:
      return DiagnosisType.Department;
    case EMedicalCategoryType.VACCINATION:
      return DiagnosisType.Vaccination;
    default:
      return undefined;
  }
};

const parseSearchDoctors = (data?: InfiniteData<SearchDoctorsByTimeSlotV2Query>): NonF2fDoctor[] | undefined => {
  return data?.pages.flatMap((page) =>
    page.searchDoctorsByTimeSlotV2.items.map((doctor) => {
      return new NonF2fDoctor(
        doctor.hospital.hospitalName,
        doctor.publicId,
        doctor.doctorName,
        doctor.medicineType,
        doctor.profileUrl ?? null,
        doctor.readyForVideoCall,
        doctor.professionalDepartments[0]?.professionalDepartmentName ?? null,
        new NonF2fDoctorReviewInfo(doctor.reviewMetric.averageRating, doctor.reviewMetric.totalCount),
        null,
        null
      );
    })
  );
};

const SEARCH_DOCTORS_LIMIT = 10;

const useSearchDoctorsInfinity = ({
  categoryType,
  categoryId = '',
  targetDate,
  startTimeSlotValue,
  endTimeSlotValue,
  sortType,
  specialistOnly = false,
  highReviewRating,
  highReviewCount,
  geoPoint,
  enabled = true,
}: Props) => {
  const parsedSortType = parseSortType(sortType);

  const {
    data: searchDoctors,
    isLoading: isLoadingSearchDoctors,
    fetchNextPage: fetchNextPageSearchDoctors,
    hasNextPage: hasNextPageSearchDoctors = false,
  } = useInfiniteQuery<SearchDoctorsByTimeSlotV2Query>(
    useSearchDoctorsByTimeSlotV2Query.getKey({
      request: {
        condition: {
          medicalCategory: {
            category: parseCategoryType(categoryType),
            id: categoryId,
          },
          specialistOnly,
          highReviewRating,
          highReviewCount,
          targetDate,
          startTimeSlotValue,
          endTimeSlotValue,
          geoPoint: geoPoint
            ? {
                latitude: geoPoint.latitude,
                longitude: geoPoint.longitude,
              }
            : undefined,
        },
        page: 1,
        limit: SEARCH_DOCTORS_LIMIT,
        sortType: parsedSortType,
      },
      diagnosisType: parseDiagnosisType(categoryType),
      diagnosisTypeId: categoryId,
    }),
    ({ pageParam }) => {
      const requestPage = pageParam ?? 1;
      return useSearchDoctorsByTimeSlotV2Query.fetcher({
        request: {
          condition: {
            medicalCategory: {
              category: parseCategoryType(categoryType),
              id: categoryId,
            },
            specialistOnly,
            highReviewRating,
            highReviewCount,
            targetDate,
            startTimeSlotValue,
            endTimeSlotValue,
            geoPoint: geoPoint
              ? {
                  latitude: geoPoint.latitude,
                  longitude: geoPoint.longitude,
                }
              : undefined,
          },
          page: requestPage,
          limit: SEARCH_DOCTORS_LIMIT,
          sortType: parsedSortType,
        },
        diagnosisType: parseDiagnosisType(categoryType),
        diagnosisTypeId: categoryId,
      })();
    },
    {
      enabled:
        enabled && !!categoryId && !!categoryType && !!targetDate && startTimeSlotValue >= 0 && endTimeSlotValue >= 0,
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.searchDoctorsByTimeSlotV2.items.length === SEARCH_DOCTORS_LIMIT) {
          return pages.length + 1;
        }
        return undefined;
      },
    }
  );

  return {
    searchDoctors: parseSearchDoctors(searchDoctors),
    isLoadingSearchDoctors,
    fetchNextPageSearchDoctors,
    hasNextPageSearchDoctors,
  };
};

export default useSearchDoctorsInfinity;
